import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import { TableProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useMemo } from 'react'

import {
  useIdentityRolesServiceDeleteRoleEndpoint,
  useIdentityRolesServiceGetRolesListEndpointKey
} from 'src/api/queries'
import { ApiError, GetRolesListResponse, Permissions } from 'src/api/requests'
import { DeleteConfirm, TableActionButtons, Text } from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type RolesTableProps = {
  title: string
  data?: GetRolesListResponse
  onEdit: (id: string) => void
} & Omit<TableProps, 'title'>

export const RolesTable = ({
  data,
  title,
  onEdit,
  ...props
}: RolesTableProps) => {
  const palette = useTheme()
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const { hasPermission } = useUser()

  const { mutate } = useIdentityRolesServiceDeleteRoleEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useIdentityRolesServiceGetRolesListEndpointKey]
      })
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const getItems = useMemo(
    () => (id: string, type: string) => {
      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => onEdit(id)
        },
        ...(type !== 'system'
          ? [
              {
                key: 'delete',
                label: (
                  <DeleteConfirm
                    title={t('roles.delete_confirm_title')}
                    onConfirm={() => mutate({ id })}
                  />
                ),
                danger: true
              }
            ]
          : [])
      ]
    },
    [t, data]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'title_role',
        title: t('roles.title_role'),
        render: ({ name, description }) => (
          <Flex vertical>
            <Text weight={700}>{name}</Text>

            <Text weight={400} color={palette.colorTextSecondary}>
              {description}
            </Text>
          </Flex>
        )
      },

      {
        key: 'permissions',
        title: t('roles.permissions'),
        dataIndex: 'permissions',
        width: 132
      },
      ...(hasPermission([
        Permissions.IDENTITY_USER_ROLES_UPDATE_ALL,
        Permissions.IDENTITY_USER_ROLES_UPDATE_INSTITUTION
      ])
        ? [
            {
              key: 'actions',
              align: 'center' as const,
              width: 64,
              render: ({ id, type }: { id: string; type: string }) => (
                <TableActionButtons items={getItems(id, type)} />
              )
            }
          ]
        : [])
    ],
    [t]
  )

  return (
    <Flex vertical gap={16}>
      <Text>{`${title} (${data?.data?.length ?? 0})`}</Text>

      <Table
        rowKey="id"
        columns={columns}
        scroll={{ x: 'max-content' }}
        dataSource={data?.data}
        bordered
        pagination={false}
        {...props}
      />
    </Flex>
  )
}
